/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {DoorHistoryItem} from '@ion/core/src/types/entities';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {TrailerDoorWidgetIcon} from '@ion/dls/src/icons/TrailerDoorWidgetIcon';
import {getDoorStatusValue, getTimeDifference} from '../../components/smarterTrailer/helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export type DoorStatusWidgetProps = {
  doorStatus: DoorHistoryItem;
};

/**
 * Defines the Door Status Widget component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 * import React from 'react';
 * import {DoorStatusWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <DoorStatusWidget doorStatus={doorStatus} />
 *
 * ```
 */
export const DoorStatusWidget = ({
  doorStatus
}: DoorStatusWidgetProps) => {
  const {doorState, messageTime: timeStamp} = doorStatus;

  const label       = translate('common:smarterTrailer.door.label');
  const value       = getDoorStatusValue(doorState);
  const today       = getTimeDifference(timeStamp);
  const messageDate = dateFormatter(timeStamp, UserPreferences.getSystemDateFormat());
  const messageTime = isNil(today) ? timeFormatter(timeStamp, UserPreferences.getSystemTimeFormat()) : today;

  return (
    <WidgetContainer>
      <TrailerDoorWidgetIcon size={30} />
      <WidgetDescription label={label}
        value={value}
        messageTime={messageTime}
        messageDate={messageDate}
        offline={isNil(status)} />
    </WidgetContainer>
  );
};

export default DoorStatusWidget;
